import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Grid,
  IconButton,
  Input,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  ListItemDecorator,
  Menu,
  MenuItem,
  Sheet,
  Tab,
  TabList,
  Tabs,
  Textarea,
  Tooltip,
  Typography,
} from "@mui/joy";
import { DialogContent, DialogTitle } from "@mui/material";
import ColorPicker from "atoms/ColorPicker";
import DialogClose from "atoms/DialogClose/DialogClose";
import { AppConfig } from "environement";
import { useAskQuestion } from "features/context/AskQuestion/AskQuestion";
import { SoloButton } from "features/projects/ProjectMissionControl/utils";
import moment, { Moment } from "moment";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  formOpenSelector,
  selectedProjectIDSelector,
  selectedRosterPresetIDSelector,
  setFormOpen,
  setSelectedBookletID,
  setSelectedProjectRosterID,
  setSelectedRosterPresetID,
} from "reducers/rhapsody";
import {
  layoutInternalSelector,
  layoutUtilsSelector,
  selectionSelector,
} from "reducers/v2/missionControl";
import { useUpdateCurrentCompanyMutation } from "redux/company/companyEndpoints";
import {
  useCompanySettings,
  useCurrentCompany,
} from "redux/company/companyHooks";
import { useProject } from "redux/project/projectHooks";
import { useSections } from "redux/section/sectionHooks";
import {
  projectDefault,
  RosterOptions,
  templateDefault,
} from "./projectRoster";
import { Booklet } from "entities/booklet";
import { LayoutUtils } from "features/projects/ProjectMissionControl/LayoutUtils";
import { InternalPosition } from "entities/internal/InternalPosition";
import { Selection } from "entities/selection";
import { Internal } from "entities/internal";
import { readyStage } from "entities/stage/helper";
import { MercuryJob } from "entities/mercuryJob";
import { useCreateBookletMutation } from "redux/booklet/bookletEndpoints";
import { renderToString } from "react-dom/server";

const fonts = ["Helvetica", "Courier", "Times-Roman"];

/**
 *
 * @returns {ReactElement} projectPieceShorthand page
 */
export function ProjectBookletRoster() {
  const open = useSelector(formOpenSelector("bookletRoster"));
  const [booklet, setBooklet] = useState(new Booklet("{}"));
  const projectID = useSelector(selectedProjectIDSelector);
  const [projectPieceIDs, setProjectPieceIDs] = useState([]);
  const [workSessionIDs, setWorkSessionIDs] = useState([]);
  const [createBooklet] = useCreateBookletMutation();
  const utils = useSelector(layoutUtilsSelector);
  const workSessions = utils?.workSessions ?? [];
  const projectPieces = utils?.projectPieces ?? [];
  const piecesMap = utils?.piecesMap ?? {};
  const { sectionsMap, sections } = useSections();
  const selection = useSelector(selectionSelector);
  const selectedProjectPieceIDs = selection?.selectedProjectPieceIDs;
  const selectedWorkSessionIDs = selection?.selectedWorkSessionIDs;
  const { project } = useProject(projectID);
  const dispatch = useDispatch();

  const settings = useCompanySettings();
  const { company } = useCurrentCompany();
  const [updateCompany] = useUpdateCurrentCompanyMutation();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
  const internal = useSelector(layoutInternalSelector);
  const nameRef = useRef<HTMLInputElement>();
  const titleRef = useRef();
  const subtitleRef = useRef();
  const tbdLabelRef = useRef();
  const [options, setOptions] = useState<Partial<RosterOptions>>();
  const selectedRosterPresetID = useSelector(selectedRosterPresetIDSelector);

  const preset = settings?.rosterPresets?.find(
    (r) => r.id === selectedRosterPresetID
  );

  useEffect(() => {
    if (!options && project) {
      setOptions(project?.template ? templateDefault : projectDefault);
    }
  }, [project]);

  useEffect(() => {
    if (settings.rosterOptions && !project?.template && !preset) {
      setOptions({
        ...settings.rosterOptions,
        title: "",
        subtitle: "",
        excludeSectionIDs: [],
      });
    }
  }, [settings, project]);

  useEffect(() => {
    setProjectPieceIDs(
      Object.keys(selectedProjectPieceIDs)
        .map((e) => (selectedProjectPieceIDs[e] ? parseInt(e) : undefined))
        .filter((e) => e)
    );
  }, [selectedProjectPieceIDs]);

  useEffect(() => {
    setWorkSessionIDs(
      Object.keys(selectedWorkSessionIDs)
        .map((e) => (selectedWorkSessionIDs[e] ? parseInt(e) : undefined))
        .filter((e) => e)
    );
  }, [selectedWorkSessionIDs]);

  const selectAllProjectPieces = () => {
    setProjectPieceIDs(
      projectPieces.reduce((a, v) => {
        a.push(v.id);
        return a;
      }, [])
    );
  };

  const selectAllWorkSessions = () => {
    setWorkSessionIDs(
      workSessions.reduce((a, v) => {
        a.push(v.id);
        return a;
      }, [])
    );
  };

  const onClose = () => {
    const _companyOptions = { ...options };
    delete _companyOptions.active;
    delete _companyOptions.workSessionIDs;
    delete _companyOptions.projectPieceIDs;
    delete _companyOptions.excludeSectionIDs;

    if (projectID)
      updateCompany({
        ...company,
        settings: JSON.stringify({
          ...settings,
          rosterOptions: _companyOptions,
        }),
      });
    dispatch(setFormOpen({ isOpen: false, formID: "bookletRoster" }));
    dispatch(setSelectedProjectRosterID(null));
    dispatch(setSelectedRosterPresetID(null));
  };

  if (!options) return <Box />;

  return (
    <DialogClose open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
      >
        Roster Booklet Configuration
      </DialogTitle>
      <Divider />
      <DialogContent
        sx={{
          display: "flex",
          p: 0,
          height: "calc(100vh - 160px)",
          position: "relative",
        }}
      >
        <Box sx={{ flex: 1 }}>
          {roster(utils, internal, options, selection)}
        </Box>
        <Divider />
        <Box
          sx={{
            background: "#fafafa",
            width: 320,
            display: "flex",
            gap: 1,
            flexDirection: "column",
            // maxHeight: "calc(100vh - 160px)",
            overflowY: "auto",
            minHeight: "auto",
            height: "100%",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, p: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                level="h6"
                startDecorator={<i className="fa-solid fa-gear"></i>}
              >
                Configuration
              </Typography>
              {settings.rosterPresets ? (
                <>
                  <Chip
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    endDecorator={<i className="fa-solid fa-chevron-down"></i>}
                    color="neutral"
                    sx={{ background: "white" }}
                    size="sm"
                    variant="outlined"
                  >
                    Apply Preset
                  </Chip>
                  <Menu
                    size="sm"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    sx={{ zIndex: 9999 }}
                  >
                    {settings.rosterPresets?.map((e) => (
                      <MenuItem
                        onClick={() => {
                          nameRef.current.value = e.name;
                          setAnchorEl(null);
                          setOptions((o) => ({ ...o, ...e.options }));
                        }}
                        key={e.id}
                      >
                        {e.name}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              ) : (
                []
              )}
            </Box>
            <Box>
              <Typography level="body2">Title</Typography>
              <Input
                onBlur={() => {
                  setOptions((o) => ({
                    ...o,
                    title: (titleRef as any).current.value,
                  }));
                }}
                slotProps={{ input: { ref: titleRef } }}
                defaultValue={options.title ?? ""}
                placeholder={project?.name}
                variant="outlined"
              />
            </Box>
            <Box>
              <Typography level="body2">Subtitle</Typography>
              <Textarea
                onBlur={() => {
                  setOptions((o) => ({
                    ...o,
                    subtitle: (subtitleRef as any).current.value,
                  }));
                }}
                slotProps={{ textarea: { ref: subtitleRef } }}
                defaultValue={options.subtitle ?? ""}
                placeholder="Ex: Conducted by David Afkham, Ray Chen Violin Soloist"
                maxRows={5}
                minRows={2}
                variant="outlined"
              />
            </Box>
            <Box>
              <Typography level="body2">Font</Typography>
              <Tabs
                aria-label="Plain tabs"
                value={options.font ?? "Helvetica"}
                size="sm"
                onChange={(event, value) =>
                  setOptions((o) => ({
                    ...o,
                    font: value as "Helvetica" | "Courier" | "Times-Roman",
                  }))
                }
                sx={{ borderRadius: "lg" }}
              >
                <TabList variant="outlined">
                  {fonts.map((f) => (
                    <Tab
                      variant={options.font === f ? "soft" : "plain"}
                      color={options.font === f ? "primary" : "neutral"}
                      key={f}
                      sx={{ fontFamily: f }}
                      value={f}
                    >
                      {f}
                    </Tab>
                  ))}
                </TabList>
              </Tabs>
            </Box>
            <Box>
              <Typography level="body2">Font Size</Typography>
              <Tabs
                aria-label="Plain tabs"
                value={options.fontSizeMd}
                size="sm"
                onChange={(event, value) =>
                  setOptions((o) => ({
                    ...o,
                    fontSizeMd: value as number,
                    fontSizeSm: (value as number) - 3,
                  }))
                }
                sx={{ borderRadius: "lg" }}
              >
                <TabList variant="outlined">
                  <Tab
                    value={10}
                    variant={options.fontSizeMd === 10 ? "soft" : "plain"}
                    color={options.fontSizeMd === 10 ? "primary" : "neutral"}
                    sx={{ fontSize: 10 }}
                  >
                    Small
                  </Tab>
                  <Tab
                    value={12}
                    variant={options.fontSizeMd === 12 ? "soft" : "plain"}
                    color={options.fontSizeMd === 12 ? "primary" : "neutral"}
                    sx={{ fontSize: 12 }}
                  >
                    Medium
                  </Tab>
                  <Tab
                    value={14}
                    variant={options.fontSizeMd === 14 ? "soft" : "plain"}
                    color={options.fontSizeMd === 14 ? "primary" : "neutral"}
                    sx={{ fontSize: 14 }}
                  >
                    Large
                  </Tab>
                </TabList>
              </Tabs>
            </Box>
            <Box>
              <Typography level="body2">Columns</Typography>
              <Tabs
                aria-label="Plain tabs"
                value={options.columns ?? 4}
                size="sm"
                onChange={(event, value) =>
                  setOptions((o) => ({ ...o, columns: value as number }))
                }
                sx={{ borderRadius: "lg" }}
              >
                <TabList variant="outlined">
                  {[2, 3, 4, 5].map((i) => (
                    <Tab
                      variant={(options?.columns ?? 4) === i ? "soft" : "plain"}
                      color={
                        (options?.columns ?? 4) === i ? "primary" : "neutral"
                      }
                      value={i}
                      key={i}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          width: "100%",
                          height: 20,
                          alignItems: "stretch",
                        }}
                      >
                        {Array.from(Array(i)).map((e) => (
                          <Sheet
                            sx={{ flex: 1, height: 20, borderRadius: 2 }}
                            variant="solid"
                            key={e}
                          ></Sheet>
                        ))}
                      </Box>
                    </Tab>
                  ))}
                </TabList>
              </Tabs>
            </Box>
            <Box>
              <Typography level="body2">Content</Typography>
              <Sheet
                variant="outlined"
                sx={{ background: "white", borderRadius: "16px", p: 1 }}
              >
                <List sx={{ gap: 1 }}>
                  {!project?.template ? (
                    <ListItem>
                      <ListItemButton
                        onClick={() =>
                          setOptions((e) => ({
                            ...e,
                            date: !(e.date ?? true),
                          }))
                        }
                      >
                        <ListItemDecorator>
                          <Checkbox size="sm" checked={options.date ?? true} />
                        </ListItemDecorator>
                        <ListItemContent>
                          <Typography
                            level="body2"
                            startDecorator={
                              <i className="fa-solid fa-calendar"></i>
                            }
                          >
                            <Box
                              sx={{ display: "flex", flexDirection: "column" }}
                            >
                              Show Date(s)
                            </Box>
                          </Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  ) : (
                    []
                  )}
                  {!project?.template ? (
                    <ListItem>
                      <ListItemButton
                        onClick={() =>
                          setOptions((e) => ({
                            ...e,
                            venue: !(e.venue ?? true),
                          }))
                        }
                      >
                        <ListItemDecorator>
                          <Checkbox size="sm" checked={options.venue ?? true} />
                        </ListItemDecorator>
                        <ListItemContent>
                          <Typography
                            level="body2"
                            startDecorator={
                              <i className="fa-solid fa-location-dot"></i>
                            }
                          >
                            <Box
                              sx={{ display: "flex", flexDirection: "column" }}
                            >
                              Show Venue
                            </Box>
                          </Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  ) : (
                    []
                  )}
                  {!project?.template ? (
                    <ListItem>
                      <ListItemButton
                        onClick={() =>
                          setOptions((e) => ({
                            ...e,
                            repertoire: !(e.repertoire ?? true),
                          }))
                        }
                      >
                        <ListItemDecorator>
                          <Checkbox
                            size="sm"
                            checked={options.repertoire ?? true}
                          />
                        </ListItemDecorator>
                        <ListItemContent>
                          <Typography
                            level="body2"
                            startDecorator={
                              <i className="fa-solid fa-music"></i>
                            }
                          >
                            <Box
                              sx={{ display: "flex", flexDirection: "column" }}
                            >
                              Show Repertoire
                            </Box>
                          </Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  ) : (
                    []
                  )}
                  {!project?.template ? (
                    <ListItem>
                      <ListItemButton
                        onClick={() =>
                          setOptions((e) => ({
                            ...e,
                            tbd: !(e.tbd ?? true),
                          }))
                        }
                      >
                        <ListItemDecorator>
                          <Checkbox
                            size="sm"
                            checked={!(options.tbd ?? true)}
                          />
                        </ListItemDecorator>
                        <ListItemContent>
                          <Typography
                            level="body2"
                            startDecorator={
                              <i className="fa-solid fa-block-question"></i>
                            }
                          >
                            <Box
                              sx={{ display: "flex", flexDirection: "column" }}
                            >
                              Show Unconfirmed Musicians
                              <Typography level="body4">
                                Shows "
                                {options.tbdLabel ? options.tbdLabel : "TBD"}"
                                instead when turned off
                              </Typography>
                            </Box>
                          </Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  ) : (
                    []
                  )}
                  {!project?.template ? (
                    <ListItem>
                      <ListItemButton
                        onClick={() =>
                          setOptions((e) => ({
                            ...e,
                            stageColor: !(e.stageColor ?? true),
                          }))
                        }
                      >
                        <ListItemDecorator>
                          <Checkbox
                            size="sm"
                            checked={options.stageColor ?? true}
                          />
                        </ListItemDecorator>
                        <ListItemContent>
                          <Typography
                            level="body2"
                            startDecorator={
                              <i className="fa-solid fa-palette"></i>
                            }
                          >
                            <Box
                              sx={{ display: "flex", flexDirection: "column" }}
                            >
                              Show Stage Color
                              <Typography level="body4">
                                Musician name will be in the status' color
                              </Typography>
                            </Box>
                          </Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  ) : (
                    []
                  )}
                  <ListItem>
                    <ListItemButton
                      onClick={() =>
                        setOptions((e) => ({
                          ...e,
                          emptyChair: !(e.emptyChair ?? true),
                        }))
                      }
                    >
                      <ListItemDecorator>
                        <Checkbox
                          size="sm"
                          checked={options.emptyChair ?? true}
                        />
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography
                          level="body2"
                          startDecorator={<i className="fa-solid fa-chair" />}
                        >
                          <Box
                            sx={{ display: "flex", flexDirection: "column" }}
                          >
                            Show Empty Chairs
                          </Box>
                        </Typography>
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                  <ListItem>
                    <ListItemButton
                      onClick={() =>
                        setOptions((e) => ({
                          ...e,
                          chairNumber: !(e.chairNumber ?? true),
                        }))
                      }
                    >
                      <ListItemDecorator>
                        <Checkbox
                          size="sm"
                          checked={options.chairNumber ?? true}
                        />
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography
                          level="body2"
                          startDecorator={
                            <i className="fa-solid fa-hashtag"></i>
                          }
                        >
                          <Box
                            sx={{ display: "flex", flexDirection: "column" }}
                          >
                            Show Chair Number
                          </Box>
                        </Typography>
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                  <ListItem>
                    <ListItemButton
                      onClick={() =>
                        setOptions((e) => ({
                          ...e,
                          standNumber: !(e.standNumber ?? false),
                        }))
                      }
                    >
                      <ListItemDecorator>
                        <Checkbox
                          size="sm"
                          checked={options.standNumber ?? false}
                        />
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography
                          level="body2"
                          startDecorator={
                            <i className="fa-solid fa-file-music"></i>
                          }
                        >
                          <Box
                            sx={{ display: "flex", flexDirection: "column" }}
                          >
                            Show Stand Number
                            <Typography level="body4">
                              For Strings only
                            </Typography>
                          </Box>
                        </Typography>
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                  <ListItem>
                    <ListItemButton
                      onClick={() =>
                        setOptions((e) => ({ ...e, role: !(e.role ?? true) }))
                      }
                    >
                      <ListItemDecorator>
                        <Checkbox size="sm" checked={options.role ?? true} />
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography
                          level="body2"
                          startDecorator={
                            <i className="fa-solid fa-hat-cowboy" />
                          }
                        >
                          <Box
                            sx={{ display: "flex", flexDirection: "column" }}
                          >
                            Show Role
                          </Box>
                        </Typography>
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                  <ListItem>
                    <ListItemButton
                      onClick={() =>
                        setOptions((e) => ({
                          ...e,
                          doublingInstruments: !(e.doublingInstruments ?? true),
                        }))
                      }
                    >
                      <ListItemDecorator>
                        <Checkbox
                          size="sm"
                          checked={options.doublingInstruments ?? true}
                        />
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography
                          level="body2"
                          startDecorator={
                            <i className="fa-solid fa-clarinet" />
                          }
                        >
                          <Box
                            sx={{ display: "flex", flexDirection: "column" }}
                          >
                            Show Doubling Instruments
                          </Box>
                        </Typography>
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                  <ListItem>
                    <ListItemButton
                      onClick={() =>
                        setOptions((e) => ({
                          ...e,
                          splitFamily: !(e.splitFamily ?? true),
                        }))
                      }
                    >
                      <ListItemDecorator>
                        <Checkbox
                          size="sm"
                          checked={options.splitFamily ?? true}
                        />
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography
                          level="body2"
                          startDecorator={
                            <i className="fa-solid fa-arrow-turn-down-left"></i>
                          }
                        >
                          <Box
                            sx={{ display: "flex", flexDirection: "column" }}
                          >
                            Split Family
                            <Typography level="body4">
                              Returns to a new line on new family
                            </Typography>
                          </Box>
                        </Typography>
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                  {!project?.template ? (
                    <ListItem>
                      <ListItemButton
                        onClick={() =>
                          setOptions((e) => ({
                            ...e,
                            assignmentMemo: !(e.assignmentMemo ?? true),
                          }))
                        }
                      >
                        <ListItemDecorator>
                          <Checkbox
                            size="sm"
                            checked={options.assignmentMemo ?? true}
                          />
                        </ListItemDecorator>
                        <ListItemContent>
                          <Typography
                            level="body2"
                            startDecorator={
                              <i className="fa-solid fa-memo"></i>
                            }
                          >
                            <Box
                              sx={{ display: "flex", flexDirection: "column" }}
                            >
                              Show Assignment Memo
                            </Box>
                          </Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  ) : (
                    []
                  )}
                  {!project?.template ? (
                    <ListItem>
                      <ListItemButton
                        onClick={() =>
                          setOptions((e) => ({
                            ...e,
                            talkback: !(e.talkback ?? true),
                          }))
                        }
                      >
                        <ListItemDecorator>
                          <Checkbox
                            size="sm"
                            checked={options.talkback ?? true}
                          />
                        </ListItemDecorator>
                        <ListItemContent>
                          <Typography
                            level="body2"
                            startDecorator={
                              <i className="fa-solid fa-comment"></i>
                            }
                          >
                            <Box
                              sx={{ display: "flex", flexDirection: "column" }}
                            >
                              Musician's Message
                              <Typography level="body4">
                                Shows the latest message
                              </Typography>
                            </Box>
                          </Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  ) : (
                    []
                  )}
                  <ListItem>
                    <ListItemButton
                      onClick={() =>
                        setOptions((e) => ({
                          ...e,
                          pageNumber: !(e.pageNumber ?? true),
                        }))
                      }
                    >
                      <ListItemDecorator>
                        <Checkbox
                          size="sm"
                          checked={options.pageNumber ?? true}
                        />
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography
                          level="body2"
                          startDecorator={
                            <i className="fa-regular fa-hashtag"></i>
                          }
                        >
                          <Box
                            sx={{ display: "flex", flexDirection: "column" }}
                          >
                            Show Page Number
                          </Box>
                        </Typography>
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                </List>
              </Sheet>
            </Box>
            {!project?.template && projectID ? (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography level="body2">Work Sessions:</Typography>
                  {workSessions.length > 1 ? (
                    <Typography
                      sx={{ cursor: "pointer" }}
                      onClick={selectAllWorkSessions}
                      level="body3"
                    >
                      Select All
                    </Typography>
                  ) : (
                    []
                  )}
                </Box>
                <Sheet
                  variant="outlined"
                  sx={{ background: "white", borderRadius: "16px", p: 1 }}
                >
                  <List size="sm">
                    {workSessions.map((w) => {
                      const selected = workSessionIDs.includes(w.id);
                      return (
                        <ListItemButton
                          selected={selected}
                          onClick={() => {
                            if (selected) {
                              setWorkSessionIDs((e) =>
                                e.filter((i) => i !== w.id)
                              );
                            } else {
                              setWorkSessionIDs((e) => [...e, w.id]);
                            }
                          }}
                          key={w.id}
                          sx={{ gap: 1 }}
                        >
                          <Checkbox size="sm" checked={selected ?? false} />
                          <Box>
                            <Typography level="body2">{w?.title}</Typography>
                            <Typography level="body4">
                              {w?.formatDateRange()}
                            </Typography>
                          </Box>
                          <Box sx={{ flexGrow: 1 }} />
                          <SoloButton
                            onClick={() => {
                              selectAllProjectPieces();
                              setWorkSessionIDs([w.id]);
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Sheet>
              </Box>
            ) : (
              []
            )}
            {!project?.template && projectID ? (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography level="body2">Project Pieces:</Typography>
                  {projectPieces.length > 1 ? (
                    <Typography
                      sx={{ cursor: "pointer" }}
                      onClick={selectAllProjectPieces}
                      level="body3"
                    >
                      Select All
                    </Typography>
                  ) : (
                    []
                  )}
                </Box>
                <Sheet
                  variant="outlined"
                  sx={{ background: "white", borderRadius: "16px", p: 1 }}
                >
                  <List size="sm">
                    {projectPieces.map((p) => {
                      const piece = piecesMap[p.pieceID];
                      const selected = projectPieceIDs.includes(p.id);
                      return (
                        <ListItemButton
                          selected={selected}
                          onClick={() => {
                            if (selected) {
                              setProjectPieceIDs((e) =>
                                e.filter((i) => i !== p.id)
                              );
                            } else {
                              setProjectPieceIDs((e) => [...e, p.id]);
                            }
                          }}
                          key={p.id}
                          sx={{ gap: 1 }}
                        >
                          <Checkbox size="sm" checked={selected ?? false} />
                          <Box>
                            <Typography level="body2">
                              {piece?.name ?? "Seating assignment"}
                            </Typography>
                            <Typography level="body4">
                              {piece?.composer ?? ""}
                            </Typography>
                          </Box>
                          <Box sx={{ flexGrow: 1 }} />
                          <SoloButton
                            onClick={() => {
                              selectAllWorkSessions();
                              setProjectPieceIDs([p.id]);
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Sheet>
              </Box>
            ) : (
              []
            )}
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography level="body2">Sections:</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      setOptions((e) => ({
                        ...e,
                        excludeSectionIDs: projectID
                          ? internal.sectionIDs
                          : Object.keys(sections).map((e) => parseInt(e)),
                      }));
                    }}
                    level="body3"
                  >
                    Unselect All
                  </Typography>
                  <Typography level="body3">|</Typography>
                  <Typography
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      setOptions((e) => ({ ...e, excludeSectionIDs: [] }));
                    }}
                    level="body3"
                  >
                    Select All
                  </Typography>
                </Box>
              </Box>
              <Sheet
                variant="outlined"
                sx={{ background: "white", borderRadius: "16px", p: 1 }}
              >
                <List>
                  {(internal?.sections ?? sections).map((s) => {
                    const section = projectID ? sectionsMap[s.sectionID] : s;
                    return (
                      <ListItemButton
                        onClick={() => {
                          if (options.excludeSectionIDs?.includes(section.id)) {
                            setOptions((e) => ({
                              ...e,
                              excludeSectionIDs: e.excludeSectionIDs.filter(
                                (i) => i != section.id
                              ),
                            }));
                          } else {
                            setOptions((e) => ({
                              ...e,
                              excludeSectionIDs: [
                                ...(e.excludeSectionIDs ?? []),
                                section.id,
                              ],
                            }));
                          }
                        }}
                        key={section.id}
                      >
                        <ListItemDecorator>
                          <Checkbox
                            checked={
                              !(
                                options.excludeSectionIDs?.includes(
                                  section.id
                                ) ?? false
                              )
                            }
                            size="sm"
                          />
                        </ListItemDecorator>
                        <Typography level="body2">
                          {sectionsMap[section.id]?.name}
                        </Typography>
                      </ListItemButton>
                    );
                  })}
                </List>
              </Sheet>
            </Box>
            <Box>
              <Typography level="body2">Text Colors</Typography>
              <Sheet
                variant="outlined"
                sx={{ background: "white", borderRadius: "16px", p: 1 }}
              >
                {!project?.template ? (
                  <ListItem>
                    <ListItemContent>
                      <Typography level="body2">
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          Unconfirmed Musicians
                        </Box>
                      </Typography>
                    </ListItemContent>
                    <ListItemDecorator sx={{ mr: 1 }}>
                      <ColorPicker
                        label=""
                        color={options.unconfirmedColor}
                        onChange={(e) =>
                          setOptions((o) => ({
                            ...o,
                            unconfirmedColor: e,
                          }))
                        }
                      />
                    </ListItemDecorator>
                  </ListItem>
                ) : (
                  []
                )}
                <ListItem>
                  <ListItemContent>
                    <Typography level="body2">
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        Doubling Instruments
                      </Box>
                    </Typography>
                  </ListItemContent>
                  <ListItemDecorator sx={{ mr: 1 }}>
                    <ColorPicker
                      label=""
                      color={options.doublingInstrumentsColor}
                      onChange={(e) =>
                        setOptions((o) => ({
                          ...o,
                          doublingInstrumentsColor: e,
                        }))
                      }
                    />
                  </ListItemDecorator>
                </ListItem>
                <ListItem>
                  <ListItemContent>
                    <Typography level="body2">
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        Empty Chairs
                      </Box>
                    </Typography>
                  </ListItemContent>
                  <ListItemDecorator sx={{ mr: 1 }}>
                    <ColorPicker
                      label=""
                      color={options.emptyChairColor}
                      onChange={(e) =>
                        setOptions((o) => ({
                          ...o,
                          emptyChairColor: e,
                        }))
                      }
                    />
                  </ListItemDecorator>
                </ListItem>
                <ListItem>
                  <ListItemContent>
                    <Typography level="body2">
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        Role
                      </Box>
                    </Typography>
                  </ListItemContent>
                  <ListItemDecorator sx={{ mr: 1 }}>
                    <ColorPicker
                      label=""
                      color={options.roleColor}
                      onChange={(e) =>
                        setOptions((o) => ({
                          ...o,
                          roleColor: e,
                        }))
                      }
                    />
                  </ListItemDecorator>
                </ListItem>
                {!project?.template ? (
                  <ListItem>
                    <ListItemContent>
                      <Typography level="body2">
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          Musician Message
                        </Box>
                      </Typography>
                    </ListItemContent>
                    <ListItemDecorator sx={{ mr: 1 }}>
                      <ColorPicker
                        label=""
                        color={options.talkbackColor}
                        onChange={(e) =>
                          setOptions((o) => ({
                            ...o,
                            talkbackColor: e,
                          }))
                        }
                      />
                    </ListItemDecorator>
                  </ListItem>
                ) : (
                  []
                )}
                {!project?.template ? (
                  <ListItem>
                    <ListItemContent>
                      <Typography level="body2">
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          Assignment Memo
                        </Box>
                      </Typography>
                    </ListItemContent>
                    <ListItemDecorator sx={{ mr: 1 }}>
                      <ColorPicker
                        label=""
                        color={options.memoColor}
                        onChange={(e) =>
                          setOptions((o) => ({
                            ...o,
                            memoColor: e,
                          }))
                        }
                      />
                    </ListItemDecorator>
                  </ListItem>
                ) : (
                  []
                )}
              </Sheet>
            </Box>
            <Box>
              <Typography level="body2">Other</Typography>
              <Sheet
                variant="outlined"
                sx={{
                  background: "white",
                  borderRadius: "16px",
                  p: 1,
                  gap: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box>
                  <Input
                    defaultValue={options.tbdLabel}
                    onBlur={() => {
                      setOptions((o) => ({
                        ...o,
                        tbdLabel: (tbdLabelRef as any).current.value,
                      }));
                    }}
                    slotProps={{ input: { ref: tbdLabelRef } }}
                    placeholder="TBD"
                  />
                  <Typography level="body4">
                    Change "TBD" with a custom label
                  </Typography>
                </Box>
              </Sheet>
            </Box>
          </Box>
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              p: 1,
              background: "white",
              borderTop: "solid 1px #bdbdbd",
              display: "flex",
              gap: 1,
              flexDirection: "column",
              zIndex: 9999,
              height: 100,
            }}
          >
            <Button
              fullWidth
              variant="solid"
              color="primary"
              onClick={() => {
                createBooklet({
                  projectID,
                  name: `${project.name} Roster`,
                  content: renderToString(
                    roster(utils, internal, options, selection)
                  ),
                })
                  .unwrap()
                  .then((e) => {
                    dispatch(setSelectedBookletID(e.id));
                    dispatch(setFormOpen({ isOpen: true, formID: "booklet" }));
                    onClose();
                  });
              }}
              startDecorator={<i className="fa-regular fa-users"></i>}
            >
              Save as Booklet
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </DialogClose>
  );
}

export function downloadBlob(blob, fileName) {
  const win: any = window;
  if (win.navigator && win.navigator.msSaveOrOpenBlob)
    return win.navigator.msSaveOrOpenBlob(blob);

  // For other browsers:
  // Create a link pointing to the ObjectURL containing the blob.
  const data = win.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = data;
  link.download = fileName;

  // this is necessary as link.click() does not work on the latest firefox
  link.dispatchEvent(
    new MouseEvent("click", {
      bubbles: true,
      cancelable: true,
      view: win,
    })
  );

  setTimeout(() => {
    // For Firefox it is necessary to delay revoking the ObjectURL
    win.URL.revokeObjectURL(data);
    link.remove();
  }, 100);
}

function roster(
  u: LayoutUtils,
  i: Internal,
  options: Partial<RosterOptions>,
  s: Selection
) {
  const { project } = u;
  const venueNames = getVenueNames(u, s);
  const projectDate = getProjectDate(u, s);
  return (
    <Box sx={{ p: 1, fontFamily: options.font, fontSize: options.fontSizeMd }}>
      <span
        style={{
          fontSize: options.fontSizeLg,
        }}
      >
        <b>{options.title ? options.title : project.name}</b>
      </span>
      <br />
      {options.subtitle ? <span>{options.subtitle}</span> : []}

      {options.date && u.workSessions.length > 0 ? (
        <span>{projectDate}</span>
      ) : (
        []
      )}

      {options.venue && u.workSessions.length > 0 ? (
        <span>{`${
          options.venue
            ? `${options.date ? " - " : ""}${venueNames.join(", ")}`
            : ""
        }`}</span>
      ) : (
        []
      )}

      {options.repertoire ? (
        <span>
          <br />
          Repertoire:{" "}
          {s.selectedPieces
            .reduce((a, v) => {
              a.push(v.name);
              return a;
            }, [])
            .join(", ")}
        </span>
      ) : (
        []
      )}
      <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap" }}>
        {i.sections.map((s, index) => {
          const section = u.sectionsMap[s.sectionID];
          const prevSection = index > 1 ? i.sections[index - 1] : undefined;
          if (options.excludeSectionIDs.includes(section.id)) return <div />;

          return (
            <>
              {options.splitFamily &&
              prevSection &&
              prevSection?.familyID !== section?.familyID ? (
                <div style={{ flexBasis: "100%", height: 0 }} />
              ) : undefined}
              {index % options.columns === 0 ? (
                <div style={{ flexBasis: "100%", height: 0 }} />
              ) : undefined}
              <div
                key={section.id}
                style={{ width: `${100 / options.columns}%`, marginBottom: 16 }}
              >
                {section.name.toUpperCase()}
                <Divider />
                {s.positions?.map((p) => {
                  const roleName = getRoleName(u, p);
                  const instruments = getInstrumentNames(u, p);

                  return (
                    <div key={p.id}>
                      {options.role && roleName ? (
                        <div
                          style={{
                            fontSize: options.fontSizeSm,
                            color: options.roleColor,
                          }}
                        >
                          {roleName}
                        </div>
                      ) : (
                        []
                      )}
                      {options.standNumber && s.familyID === 1 ? (
                        `${Math.floor((p.order - 1) / 2) + 1}${
                          p.order % 2 === 1 ? "a" : "b"
                        }. `
                      ) : (
                        <>{options.chairNumber ? `${p.order}. ` : ""}</>
                      )}
                      {p.visibleMusicianIDs.map((musicianID, musicianIndex) => {
                        const musician = u.musiciansMap[musicianID];
                        const continued =
                          musicianIndex < p.visibleMusicianIDs.length - 1;
                        const assignment = u.assignments.find(
                          (a) => a.musicianID === musicianID
                        );
                        const job = new MercuryJob(assignment?.job);
                        const talkback = job.talkback();
                        const stage =
                          u.stagesMap[assignment?.mercuryStageID] ?? readyStage;
                        return (
                          <span key={musicianID}>
                            {unconfirmedStages.includes(stage.id) &&
                            options.tbd ? (
                              `${options.tbdLabel}${continued ? ", " : ""}`
                            ) : musician ? (
                              <span
                                style={{
                                  color: options.stageColor
                                    ? stage.color
                                    : undefined,
                                }}
                              >
                                {musician?.fullName(false)}
                                {continued ? ", " : ""}
                              </span>
                            ) : (
                              `${
                                options.emptyChair
                                  ? `${"Empty Chair"}${continued ? ", " : ""}`
                                  : ""
                              }`
                            )}
                            {options.assignmentMemo && assignment?.memo ? (
                              <div
                                style={{
                                  color: options.memoColor,
                                  fontSize: options.fontSizeSm,
                                }}
                              >
                                <i>{`Memo: ${assignment.memo}`}</i>
                              </div>
                            ) : (
                              ""
                            )}
                            {options.talkback && talkback?.message ? (
                              <div
                                style={{
                                  color: options.talkbackColor,
                                  fontSize: options.fontSizeSm,
                                }}
                              >
                                <i>{`Message: ${talkback?.message}`}</i>
                              </div>
                            ) : (
                              ""
                            )}
                            {options.doublingInstruments &&
                            p.visibleInstrumentIDs.length > 1 ? (
                              <div
                                style={{
                                  color: options.doublingInstrumentsColor,
                                  fontSize: options.fontSizeSm,
                                }}
                              >
                                {instruments}
                              </div>
                            ) : (
                              ""
                            )}
                          </span>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </>
          );
        })}
      </div>
    </Box>
  );
}

export function getVenueNames(u: LayoutUtils, s: Selection) {
  const venues: string[] = [];
  s.selectedWorkSessions.forEach((w) => {
    if (w.studioName && !venues.includes(w.studioName))
      venues.push(w.studioName);
  });

  return venues;
}

export function getInstrumentNames(u: LayoutUtils, p: InternalPosition) {
  const instruments: string[] = [];
  if (p.visibleInstrumentIDs.length > 1) {
    p.visibleInstrumentIDs.forEach((i) => {
      instruments.push(u.instrumentsMap[i].name);
    });
  }
  return instruments.join(", ");
}

export function getProjectDate(u: LayoutUtils, s: Selection) {
  let dateFrom: Moment;
  let dateTo: Moment;

  if (s.selectedWorkSessions.length === 1)
    return s.selectedWorkSessions[0].formatDateRange();

  s.selectedWorkSessions.forEach((w) => {
    const from = moment(w.dateFromUTC);
    const to = moment(w.dateToUTC);

    if (!dateFrom) dateFrom = from;
    if (!dateTo) dateTo = to;

    if (to.isAfter(dateTo)) dateTo = to;
    if (from.isBefore(dateFrom)) dateFrom = from;
  });

  if (dateFrom.year() <= 1 && dateTo.year() <= 1) return "Date N/A";
  if (dateFrom?.isSame(dateTo, "day")) return dateFrom?.format("ll");

  return `From ${dateFrom?.format("l")} to ${dateTo?.format("l")}`;
}

export function getRoleName(u: LayoutUtils, p: InternalPosition) {
  let role: string;
  if (p.visibleSectionRoleIDs.length == 1) {
    role = u.sectionRolesMap[p.visibleSectionRoleIDs[0]].name;
  } else {
    role = "Multiple";
  }
  if (role === "Section") role = undefined;

  return role;
}

export const unconfirmedStages = [0, 1, 3, 4, 15, 19, 20, 8, 9];
